import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { Menu, X, Phone } from "lucide-react";

export default function Nav() {
  const { lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const anchor = (id) => (loc.pathname === "/" ? `#${id}` : `/#${id}`);

  const links = [
    { id: "how", label: pick(T.nav.how, lang) },
    { id: "cases", label: pick(T.nav.cases, lang) },
    { id: "stories", label: pick(T.nav.stories, lang) },
    { id: "fees", label: pick(T.nav.fees, lang) },
    { id: "faq", label: pick(T.nav.faq, lang) },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-[#D8CDBA]/60" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-brand">
          <img
            src="/rihai-logo.png"
            alt="Rihai"
            className="h-14 md:h-16 w-auto object-contain"
            data-testid="nav-logo"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={anchor(l.id)}
              className="text-sm text-[#4A4640] hover:text-[#2C4C3B] transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
          <Link to="/blog" className="text-sm text-[#4A4640] hover:text-[#2C4C3B]" data-testid="nav-link-guides">
            {lang === "hi" ? "ज़मानत गाइड" : "Bail Guides"}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center bg-[#EAE6DF] rounded-full p-1" data-testid="lang-toggle">
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1 text-xs rounded-full transition-all ${
                lang === "en" ? "bg-[#2C4C3B] text-white" : "text-[#4A4640]"
              }`}
              data-testid="lang-en"
            >
              EN
            </button>
            <button
              onClick={() => setLang("hi")}
              className={`px-3 py-1 text-xs rounded-full transition-all ${
                lang === "hi" ? "bg-[#2C4C3B] text-white" : "text-[#4A4640]"
              }`}
              data-testid="lang-hi"
            >
              हिं
            </button>
          </div>

          <a
            href={`tel:${T.phone.number}`}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#A83F32] text-[#A83F32] text-sm font-medium hover:bg-[#A83F32] hover:text-white transition"
            data-testid="nav-call"
          >
            <Phone size={14} strokeWidth={2} /> {T.phone.display}
          </a>

          <a href={anchor("lead")} className="btn-primary text-sm hidden md:inline-flex" data-testid="nav-cta">
            {pick(T.nav.cta, lang)}
          </a>

          <button
            className="lg:hidden p-2 text-[#2C4C3B]"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FAF9F6] border-t border-[#D8CDBA] px-6 py-6 space-y-4">
          {links.map((l) => (
            <a
              key={l.id}
              href={anchor(l.id)}
              onClick={() => setOpen(false)}
              className="block text-[#4A4640]"
              data-testid={`nav-mobile-${l.id}`}
            >
              {l.label}
            </a>
          ))}
          <Link to="/blog" onClick={() => setOpen(false)} className="block text-[#4A4640]" data-testid="nav-mobile-guides">
            {lang === "hi" ? "ज़मानत गाइड" : "Bail Guides"}
          </Link>
          <div className="flex items-center bg-[#EAE6DF] rounded-full p-1 w-fit">
            <button
              onClick={() => setLang("en")}
              className={`px-4 py-1 text-xs rounded-full ${lang === "en" ? "bg-[#2C4C3B] text-white" : ""}`}
              data-testid="lang-en-mobile"
            >
              EN
            </button>
            <button
              onClick={() => setLang("hi")}
              className={`px-4 py-1 text-xs rounded-full ${lang === "hi" ? "bg-[#2C4C3B] text-white" : ""}`}
              data-testid="lang-hi-mobile"
            >
              हिं
            </button>
          </div>
          <a href={anchor("lead")} className="btn-primary text-sm" onClick={() => setOpen(false)}>
            {pick(T.nav.cta, lang)}
          </a>
          <a
            href={`tel:${T.phone.number}`}
            className="btn-secondary text-sm border-[#A83F32] text-[#A83F32]"
            onClick={() => setOpen(false)}
            data-testid="nav-call-mobile"
          >
            <Phone size={14} /> {T.phone.display}
          </a>
        </div>
      )}
    </header>
  );
}
